import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { ChecklistPopover } from './checklist-popover';
import type { FilterConfig } from './filters-tray';
import { RadioListPopover } from './radio-list-popover';

type InlineFilterRowProps = {
  filters: FilterConfig[];
  /**
   * The CSS container-name of the row element.
   */
  containerName: string;
};

export const InlineFilterRow = ({ filters, containerName }: InlineFilterRowProps) => {
  return (
    <>
      {filters.map((filter) => (
        <div
          key={filter.key}
          css={[
            styles.filterItem,
            (filter.minInlineWidth ?? 0) > 0 &&
              css`
                @container ${containerName} (max-width: ${(filter.minInlineWidth ?? 0) - 1}px) {
                  display: none;
                }
              `,
          ]}
        >
          {filter.type === 'radio' ? (
            <RadioListPopover
              label={filter.title}
              fieldName={filter.key}
              leadingIcon='location'
              options={filter.items}
              radioField={filter.fieldProps}
              showValueAsLabel
              hideClear
            />
          ) : (
            <ChecklistPopover
              label={filter.title}
              fieldName={filter.key}
              leadingIcon={
                filter.key === 'timeRanges' ? 'clock' : filter.key === 'operatoryIds' ? 'department' : 'user'
              }
              options={filter.items}
              checklistField={filter.fieldProps}
            />
          )}
        </div>
      ))}
    </>
  );
};

const styles = {
  filterItem: css`
    flex-shrink: 0;
    display: flex;
    align-items: center;
  `,
};

export type UseInlineFilterRow = {
  containerRef: React.RefObject<HTMLElement>;
  containerWidth: number;
};

export const useInlineFilterRow = (): UseInlineFilterRow => {
  const containerRef = useRef<HTMLElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const updateWidth = useCallback((entries: ResizeObserverEntry[]) => {
    for (const entry of entries) {
      setContainerWidth(entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width);
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    setContainerWidth(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, [updateWidth]);

  return { containerRef, containerWidth };
};
